import Tooltip from "@material-ui/core/Tooltip";
import React, { FC } from "react";
import { Theme } from "@material-ui/core/styles";
import { styled } from "@material-ui/styles";

import SecondaryButton from "../../elements/Button/SecondaryButton";
import Select from "../../elements/Select";
import {
  BULK_ACTIONS,
  BULK_ACTION_LABELS,
  BulkAction,
  getDisabledReason,
  isActionEnabled,
} from "./campaignBulkActions";
import { CampaignTableRow } from "./campaignTableTypes";
import { EXPORT_TIME_FRAME_OPTIONS } from "./campaignExport";
import { ReportTimeFrames } from "../../../../types/campaignTypes";

type CampaignBulkActionsBarProps = {
  selectedRows: CampaignTableRow[];
  onAction: (action: BulkAction) => void;
  onClearSelection: () => void;
  isApplying: boolean;
  exportTimeFrame: ReportTimeFrames;
  onExportTimeFrameChange: (timeFrame: ReportTimeFrames) => void;
  onExport: () => void;
  isExporting: boolean;
};

const Bar = styled("div")(({ theme }: { theme: Theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: 8,
  marginBottom: 12,
  padding: "10px 14px",
  background: theme.palette.brand.gray50,
  border: `1px solid ${theme.palette.brand.gray200}`,
  borderRadius: 10,
}));

const SelectionCount = styled("span")(({ theme }: { theme: Theme }) => ({
  fontSize: 13,
  fontWeight: 600,
  color: theme.palette.brand.gray900,
  marginRight: 4,
  "&.empty": {
    fontWeight: 500,
    color: theme.palette.brand.gray500,
  },
}));

const ExportGroup = styled("div")({
  marginLeft: "auto",
  display: "flex",
  alignItems: "center",
  gap: 8,
});

const TimeFrameWrap = styled("div")({
  width: 140,
  "& .MuiInputBase-root": {
    height: 36,
  },
  "& .MuiSelect-select": {
    padding: "8px 32px 8px 12px",
  },
});

const ClearButton = styled("button")(({ theme }: { theme: Theme }) => ({
  background: "none",
  border: "none",
  padding: 0,
  cursor: "pointer",
  fontSize: 13,
  fontFamily: "inherit",
  color: theme.palette.brand.gray500,
  "&:hover": { color: theme.palette.brand.gray900 },
}));

const CampaignBulkActionsBar: FC<CampaignBulkActionsBarProps> = ({
  selectedRows,
  onAction,
  onClearSelection,
  isApplying,
  exportTimeFrame,
  onExportTimeFrameChange,
  onExport,
  isExporting,
}) => {
  const hasSelection = selectedRows.length > 0;

  // MUI's Select props here are an intersection with FormControlProps, so the
  // handler has to stay loosely typed — same as the legacy TimeFrameSelector.
  const handleTimeFrameChange = (event: any) => onExportTimeFrameChange(event.target.value as ReportTimeFrames);

  return (
    <Bar>
      <SelectionCount className={hasSelection ? "" : "empty"}>
        {hasSelection ? `${selectedRows.length} selected` : "No campaigns selected"}
      </SelectionCount>

      {BULK_ACTIONS.map((action) => {
        const isEnabled = isActionEnabled(selectedRows, action);

        return (
          <Tooltip key={action} title={isEnabled ? "" : getDisabledReason(selectedRows, action)}>
            <span>
              <SecondaryButton disabled={!isEnabled || isApplying} onClick={() => onAction(action)}>
                {BULK_ACTION_LABELS[action]}
              </SecondaryButton>
            </span>
          </Tooltip>
        );
      })}

      {hasSelection ? (
        <ClearButton type="button" onClick={onClearSelection} disabled={isApplying}>
          Clear selection
        </ClearButton>
      ) : null}

      <ExportGroup>
        <TimeFrameWrap>
          <Select
            label="Time Frame"
            value={exportTimeFrame}
            options={EXPORT_TIME_FRAME_OPTIONS}
            onChange={handleTimeFrameChange}
          />
        </TimeFrameWrap>
        <Tooltip title={hasSelection ? "" : "Select at least one campaign."}>
          <span>
            <SecondaryButton disabled={!hasSelection || isExporting || isApplying} onClick={onExport}>
              {isExporting ? "Exporting…" : "Export"}
            </SecondaryButton>
          </span>
        </Tooltip>
      </ExportGroup>
    </Bar>
  );
};

export default CampaignBulkActionsBar;
